import React, { useRef } from 'react';
import {
  getAvailableFieldsForCalculation,
  getFieldDisplayName,
  getUnknownFieldRefs,
  makeFieldRef,
  removeLastToken,
  sanitizeExpressionInput,
  testCalculation,
  detectCircularDependency,
  tokenizeExpression,
} from './formSchemaUtils';

const OPERATORS = ['(', ')', '+', '-', '*', '/'];

// A question with neither a name nor question text yet falls back to its id,
// which means nothing to the author — say so instead.
const boxLabel = (field) => {
  const displayName = getFieldDisplayName(field);
  return displayName === field.id ? 'Untitled question' : displayName;
};

// Expression builder for a calculation field. Questions are inserted as boxes
// that stay whole no matter what they are called — a question named
// "Observation/Sight score" is one token, never a division of "Observation" by
// "Sight". Only digits and operators can be typed; every other character is
// dropped, so nothing an author types can be mistaken for a question.
const CalculationEditor = ({ field, fields, updateField, alert }) => {
  const inputRef = useRef(null);
  const expression = field.expression || '';
  const tokens = tokenizeExpression(expression, fields);
  const availableFields = getAvailableFieldsForCalculation(fields, field.id);
  const hasDeletedRefs = getUnknownFieldRefs(expression, fields).length > 0;

  const setExpression = (next) => updateField(field.id, { expression: next });
  const append = (text) => setExpression(expression + text);
  const focusInput = () => inputRef.current?.focus();

  const handleTyping = (e) => {
    const typed = sanitizeExpressionInput(e.target.value);
    // The input is only a keyboard: what survives sanitizing is appended to the
    // expression and the box is emptied again.
    e.target.value = '';
    if (typed) append(typed);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Backspace' && !e.target.value) {
      e.preventDefault();
      setExpression(removeLastToken(expression));
    }
  };

  const handleTest = () => {
    if (detectCircularDependency(fields, field.id, expression)) {
      alert({
        title: 'Circular dependency detected',
        message: 'Calculation fields cannot reference each other in a cycle.',
      });
      return;
    }
    const testResult = testCalculation(expression, fields);
    if (testResult.valid) {
      alert({
        title: 'Test passed',
        message: `With every referenced question set to 1, this calculates to ${testResult.result}.`,
      });
    } else {
      alert({ title: 'Test failed', message: testResult.error });
    }
  };

  return (
    <div className="mt-2 space-y-2">
      {/* Expression canvas: boxes for questions, plain text for the arithmetic. */}
      <div
        onClick={focusInput}
        className="w-full min-h-[46px] bg-gray-600 border border-gray-500 rounded px-2 py-2 flex flex-wrap items-center gap-1 cursor-text"
      >
        {tokens.map((token, tokenIndex) => (token.type === 'field' ? (
          <span
            key={`token-${tokenIndex}`}
            title={token.field ? boxLabel(token.field) : 'This question was deleted'}
            className={`inline-flex items-center max-w-full truncate rounded-md border px-2 py-1 text-xs font-medium ${
              token.field
                ? 'border-purple-400 bg-purple-500/25 text-purple-100'
                : 'border-amber-400 bg-amber-500/20 text-amber-100'
            }`}
          >
            {token.field ? boxLabel(token.field) : 'Deleted question'}
          </span>
        ) : (
          <span key={`token-${tokenIndex}`} className="font-mono text-sm text-gray-100 whitespace-pre">
            {token.value}
          </span>
        )))}
        <input
          ref={inputRef}
          type="text"
          value=""
          onChange={handleTyping}
          onKeyDown={handleKeyDown}
          aria-label="Type numbers and operators"
          placeholder={tokens.length === 0 ? 'Pick a question below, or type numbers and operators' : ''}
          className="flex-1 min-w-[120px] bg-transparent border-none text-white placeholder-gray-400 font-mono text-sm px-1 py-0.5 focus:outline-none"
        />
      </div>

      {hasDeletedRefs && (
        <p className="text-xs text-amber-300">
          A question used by this calculation was deleted. Remove the highlighted box to fix it.
        </p>
      )}

      {/* Question boxes to insert. */}
      {availableFields.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {availableFields.map((availField) => (
            <button
              key={availField.id}
              type="button"
              onClick={() => {
                append(makeFieldRef(availField));
                focusInput();
              }}
              className="bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs max-w-full truncate"
              title={`Insert ${boxLabel(availField)}`}
            >
              {boxLabel(availField)}
            </button>
          ))}
        </div>
      ) : (
        <p className="text-xs text-gray-400">
          Add a Number question to this form before building a calculation.
        </p>
      )}

      {/* Operators, kept visually separate from the question boxes above. */}
      <div className="flex flex-wrap gap-1">
        {OPERATORS.map((op) => (
          <button
            key={op}
            type="button"
            onClick={() => {
              append(op);
              focusInput();
            }}
            className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-xs font-mono w-9"
          >
            {op}
          </button>
        ))}
        <button
          type="button"
          onClick={() => {
            setExpression(removeLastToken(expression));
            focusInput();
          }}
          disabled={!expression}
          className="bg-gray-600 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1 rounded text-xs"
          title="Delete the last box or character"
        >
          ⌫
        </button>
        <button
          type="button"
          onClick={() => setExpression('')}
          disabled={!expression}
          className="bg-purple-500/15 border border-purple-400/30 text-purple-200 hover:bg-red-500 hover:border-red-400 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed px-2 py-1 rounded text-xs transition-all duration-200"
        >
          Clear
        </button>
      </div>

      <button
        type="button"
        onClick={handleTest}
        className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-violet-500 hover:from-purple-600 hover:to-violet-600 text-white px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 shadow"
      >
        Test Calculation
      </button>
    </div>
  );
};

export default CalculationEditor;
